import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CreateExpenseUnitFormValues,
  UpdateExpenseUnitFormValues,
  createExpenseUnit,
  deleteExpenseUnit,
  getExpenseUnits,
  restoreExpenseUnit,
  updateExpenseUnit,
} from "../api/expenseUnits";
import { queryClient } from "@/api/queryClient";
import { ExpenseUnit } from "@/types/expenseUnit";
import { ApiResponse, PaginatedApiResponse, PaginationInfo } from "@/types/api";
import { AxiosRequestConfig } from "axios";

interface useExpenseUnitMutationOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export const useGetExpenseUnits = (config?: AxiosRequestConfig) => {
  return useQuery<
    PaginatedApiResponse<ExpenseUnit[]>,
    Error,
    { data: ExpenseUnit[]; pagination: PaginationInfo }
  >({
    queryKey: ["expense-units", config?.params],
    queryFn: () => getExpenseUnits(config),
    select: (response) => ({
      data: response.data,
      pagination: response.pagination,
    }),
  });
};

export const useGetExpenseUnitsOptions = (config?: AxiosRequestConfig) => {
  return useQuery<ApiResponse<ExpenseUnit[]>, Error, ExpenseUnit[]>({
    queryKey: ["expense-units-options", config?.params],
    queryFn: () => getExpenseUnits(config),
    select: (response) => response.data,
  });
};

export const useCreateExpenseUnit = (
  options?: useExpenseUnitMutationOptions<ExpenseUnit>
) => {
  return useMutation({
    mutationFn: (data: CreateExpenseUnitFormValues) => createExpenseUnit(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-units"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useUpdateExpenseUnit = (
  options?: useExpenseUnitMutationOptions<ExpenseUnit>
) => {
  return useMutation({
    mutationFn: (data: UpdateExpenseUnitFormValues) =>
      updateExpenseUnit(data.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-units"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useDeleteExpenseUnit = (
  options?: useExpenseUnitMutationOptions<ExpenseUnit>
) => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteExpenseUnit(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-units"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useRestoreExpenseUnit = (
  options?: useExpenseUnitMutationOptions<ExpenseUnit>
) => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => restoreExpenseUnit(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-units"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};
